import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Desktop breakpoint for the site header. Shared with the CSS `@media` query
 * in `sections/site-header.liquid` — keep both in sync (05-javascript.md
 * § "Viewport checks share the CSS breakpoint").
 *
 * @type {string}
 */
const DESKTOP_BREAKPOINT = '(min-width: 1024px)';

/**
 * Delay before a hovered mega-panel closes after the pointer leaves the
 * trigger or the panel itself. Gives the user time to move the pointer from
 * the trigger into the panel without it closing underneath them.
 *
 * @type {number}
 */
const PANEL_CLOSE_DELAY = 120;

/**
 * Site header: desktop mega-panel navigation, a ships-to state picker, and a
 * mobile drawer built on the theme's existing `<dialog-component>`.
 *
 * The "Shop by State" panel content is its own component
 * (`<header-state-panel>`, assets/header-state-panel.js). Its "Ships to you"
 * card's "Change state" button dispatches the bubbling
 * `header-state-panel:change-state` event; this component answers it by
 * closing the panel and opening the state picker (desktop only — the mobile
 * drawer opens its own picker as a floating popover).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} navTriggers - The three mega-panel triggers (state/ranch/cost); ranch is a link, so its click also navigates.
 * @property {HTMLElement[]} panels - The three mega-panel containers, in the same order as navTriggers.
 * @property {HTMLElement} barInner - The desktop bar's centred column (the available width).
 * @property {HTMLElement} barContent - The desktop bar's flex row (its scrollWidth is the needed width).
 * @property {HTMLElement} chip - The desktop "Ships to" chip that opens the state picker.
 * @property {HTMLElement} picker - The state-picker container (H2-owned content renders inside it).
 * @property {import('./dialog.js').DialogComponent} mobileDrawer - The mobile drawer's dialog-component.
 *
 * @extends {Component<Refs>}
 */
class SiteHeaderComponent extends Component {
  requiredRefs = ['navTriggers', 'panels', 'chip', 'picker', 'mobileDrawer', 'barInner', 'barContent'];

  /** @type {string | null} */
  #openPanelId = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  #desktopQuery = matchMedia(DESKTOP_BREAKPOINT);

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    for (const trigger of this.refs.navTriggers) {
      trigger.addEventListener('pointerenter', this.#openPanelOnTriggerHover, { signal });
      trigger.addEventListener('pointerleave', this.#scheduleCloseOnPointerLeave, { signal });
      trigger.addEventListener('click', this.#togglePanelOnTriggerClick, { signal });
    }

    for (const panel of this.refs.panels) {
      panel.addEventListener('pointerenter', this.#cancelScheduledClose, { signal });
      panel.addEventListener('pointerleave', this.#scheduleCloseOnPointerLeave, { signal });
    }

    for (const link of this.querySelectorAll('.js-site-header-direct-link')) {
      link.addEventListener('pointerenter', this.#closePanelOnDirectLinkHover, { signal });
    }

    this.refs.chip.addEventListener('click', this.#togglePickerOnChipClick, { signal });
    this.querySelector('.js-site-header-mobile-picker')?.addEventListener('toggle', this.#syncMobileChipOnPickerToggle, {
      signal,
    });
    this.addEventListener('header-state-panel:change-state', this.#openPickerOnChangeStateRequest, { signal });

    this.addEventListener('keydown', this.#closeOnEscape, { signal });
    document.addEventListener('pointerdown', this.#closeOnOutsidePointerDown, { signal, capture: true });

    this.#desktopQuery.addEventListener('change', this.#closeDrawerOnDesktopChange, { signal });
    this.refs.mobileDrawer.addEventListener('dialog:close', this.#collapseAccordionsOnDrawerClose, { signal });

    this.#resizeObserver = new ResizeObserver(this.#recordHeaderHeight);
    this.#resizeObserver.observe(this);

    // Fit modes: the state name in the chip changes at runtime (destination
    // store), so the needed width is only known after render.
    this.#fitObserver = new ResizeObserver(this.#fitDesktopBar);
    this.#fitObserver.observe(this.refs.barInner);
    this.#fitObserver.observe(this.refs.chip);
    document.addEventListener('destination-state:change', this.#fitDesktopBar, { signal });
    this.#recordHeaderHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    this.#fitObserver?.disconnect();
    this.#cancelScheduledClose();
  }

  /**
   * Records the header's rendered height on `document.body` as `--header-height`
   * so other sections can offset themselves (sticky-header anchors, etc.).
   * Writing a custom property for a continuous, measured value is the
   * sanctioned exception to "JS never sets inline styles" (05-javascript.md).
   */
  #recordHeaderHeight = () => {
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
  };

  /** @type {ResizeObserver | undefined} */
  #fitObserver;

  /**
   * Keeps the desktop bar on one line without overlap. The bar's column already
   * grows with its content up to the viewport (CSS); when the viewport itself is
   * too narrow, step down: `_tight` (smaller gaps/paddings), then `_compact`
   * (state shown as its two-letter code). Measured with the modes off so a
   * wider viewport relaxes them again. Classes only — no inline styles.
   */
  #fitDesktopBar = () => {
    if (!this.#desktopQuery.matches) {
      this.classList.remove('_tight', '_compact');
      return;
    }

    const { barInner, barContent } = this.refs;
    const fits = () => barContent.scrollWidth <= barInner.clientWidth + 1;

    this.classList.remove('_tight', '_compact');
    if (fits()) return;

    this.classList.add('_tight');
    if (fits()) return;

    this.classList.add('_compact');
  };

  /**
   * Opens the hovered trigger's mega-panel immediately.
   * @param {PointerEvent} event
   */
  #openPanelOnTriggerHover = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.#openPanel(trigger.dataset.panel ?? null, trigger);
  };

  /**
   * Toggles the clicked trigger's mega-panel open or closed.
   * @param {MouseEvent} event
   */
  #togglePanelOnTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    const panelId = trigger.dataset.panel ?? null;

    if (this.#openPanelId === panelId) {
      this.#closePanel();
      trigger.focus();
    } else {
      this.#openPanel(panelId, trigger);
    }
  };

  /**
   * Schedules the open panel to close after PANEL_CLOSE_DELAY, giving the
   * pointer time to travel from the trigger into the panel (or back).
   */
  #scheduleCloseOnPointerLeave = () => {
    this.#cancelScheduledClose();
    this.#closeTimer = setTimeout(() => this.#closePanel(), PANEL_CLOSE_DELAY);
  };

  /**
   * Cancels a pending scheduled close — called when the pointer re-enters
   * the trigger or the open panel.
   */
  #cancelScheduledClose = () => {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /**
   * Closes any open mega-panel when the pointer hovers a direct nav link.
   */
  #closePanelOnDirectLinkHover = () => {
    this.#closePanel();
  };

  /**
   * @param {string | null} panelId
   * @param {HTMLElement} _trigger
   */
  #openPanel(panelId, _trigger) {
    if (!panelId) return;

    this.#cancelScheduledClose();
    this.#closePicker();

    for (const panel of this.refs.panels) {
      const isTarget = panel.dataset.panel === panelId;
      panel.hidden = !isTarget;
      panel.inert = !isTarget;
    }

    for (const trigger of this.refs.navTriggers) {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.panel === panelId));
    }

    this.#openPanelId = panelId;
  }

  #closePanel() {
    if (this.#openPanelId === null) return;

    for (const panel of this.refs.panels) {
      panel.hidden = true;
      panel.inert = true;
    }

    for (const trigger of this.refs.navTriggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.#openPanelId = null;
  }

  /**
   * Toggles the state picker open or closed when the ships-to chip is clicked.
   */
  #togglePickerOnChipClick = () => {
    this.#closePanel();

    if (this.refs.picker.hidden) {
      this.#openPicker();
    } else {
      this.#closePicker();
    }
  };

  /**
   * Answers the state panel's "Change state" request
   * (`header-state-panel:change-state`, dispatched by `<header-state-panel>`
   * inside the open mega-panel): closes the panel, opens the state picker and
   * moves focus into it — to the selected option, else the first option, else
   * back to the chip — so the keyboard user lands where the choice happens.
   * Escape and outside pointerdown then close the picker through the existing
   * handlers. Desktop only: the picker lives in the desktop bar.
   */
  #openPickerOnChangeStateRequest = () => {
    if (!this.#desktopQuery.matches) return;

    this.#closePanel();
    this.#openPicker();

    const { picker, chip } = this.refs;
    const focusTarget =
      picker.querySelector('.state-picker__option._selected, .state-picker__option[aria-current="true"]') ??
      picker.querySelector('.state-picker__option') ??
      chip;

    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  };

  /**
   * Mirrors the mobile state picker popover on its chip: `aria-expanded` and
   * the chip's open look follow the popover, and focus moves into the picker
   * — the selected option, else the first — without scrolling the list.
   * Closing returns focus to the chip on its own (popover behavior).
   *
   * @param {Event} event
   */
  #syncMobileChipOnPickerToggle = (event) => {
    const { newState } = /** @type {ToggleEvent} */ (event);
    const picker = event.currentTarget;
    if (!(picker instanceof HTMLElement)) return;

    const chip = this.querySelector(`[popovertarget="${CSS.escape(picker.id)}"]`);
    chip?.setAttribute('aria-expanded', String(newState === 'open'));
    if (newState !== 'open') return;

    const target =
      picker.querySelector('.state-picker__option._selected, .state-picker__option[aria-current="true"]') ??
      picker.querySelector('.state-picker__option');

    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  };

  #openPicker() {
    this.refs.picker.hidden = false;
    this.refs.picker.inert = false;
    this.refs.chip.setAttribute('aria-expanded', 'true');
  }

  #closePicker() {
    if (this.refs.picker.hidden) return;

    this.refs.picker.hidden = true;
    this.refs.picker.inert = true;
    this.refs.chip.setAttribute('aria-expanded', 'false');
  }

  /**
   * Escape closes whichever of panel/picker is open and returns focus to
   * its trigger.
   * @param {KeyboardEvent} event
   */
  #closeOnEscape = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#openPanelId !== null) {
      const panelId = this.#openPanelId;
      const trigger = this.refs.navTriggers.find((candidate) => candidate.dataset.panel === panelId);
      this.#closePanel();
      trigger?.focus();
      return;
    }

    if (!this.refs.picker.hidden) {
      this.#closePicker();
      this.refs.chip.focus();
    }
  };

  /**
   * A pointerdown outside the header closes any open panel or picker.
   * @param {PointerEvent} event
   */
  #closeOnOutsidePointerDown = (event) => {
    if (!isClickedOutside(event, this)) return;
    this.#closePanel();
    this.#closePicker();
  };

  /**
   * Closes the mobile drawer when the viewport crosses into desktop —
   * matches the CSS breakpoint (DESKTOP_BREAKPOINT).
   * @param {MediaQueryListEvent} event
   */
  #closeDrawerOnDesktopChange = (event) => {
    if (!event.matches) return;
    if (typeof this.refs.mobileDrawer.closeDialog === 'function') {
      this.refs.mobileDrawer.closeDialog();
    }
  };

  /**
   * Collapses every accordion in the drawer once it finishes closing, so it
   * reopens fresh next time, and dismisses any popover left open inside it
   * (the mobile state panel's floating state picker).
   */
  #collapseAccordionsOnDrawerClose = () => {
    for (const details of this.refs.mobileDrawer.querySelectorAll('details')) {
      if (details instanceof HTMLDetailsElement) details.open = false;
    }

    for (const popover of this.refs.mobileDrawer.querySelectorAll(':popover-open')) {
      if (popover instanceof HTMLElement) popover.hidePopover();
    }
  };
}

if (!customElements.get('site-header-component')) {
  customElements.define('site-header-component', SiteHeaderComponent);
}
